import type { ReluxDraft, SpaceDraft } from './types'

export type UploadedFile = {
  fileId: string
  storagePath: string
  name: string
  mime: string
  size: number
  category: string
  spaceClientId?: string
  markup?: unknown
}

const num = (v: string) => {
  const t = v.trim().replace(',', '.')
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}
const str = (v: string | undefined) => (v && v.trim() !== '' ? v.trim() : null)

/** Sports field details, as data for the designer and as a readable line in the notes. */
function sports(s: SpaceDraft) {
  const d = {
    poles: num(s.sportsPoles ?? ''),
    pole_height_m: num(s.sportsPoleHeightM ?? ''),
    layout: str(s.sportsLayout),
    level: str(s.sportsLevel),
  }
  if (Object.values(d).every((v) => v == null)) return null
  const line = [
    d.poles != null && `${d.poles} poles`,
    d.pole_height_m != null && `${d.pole_height_m} m high`,
    d.layout,
    d.level && `${d.level} level`,
  ]
    .filter(Boolean)
    .join(', ')
  return { data: d, line: `Sports lighting: ${line}.` }
}

function space(s: SpaceDraft, i: number) {
  const sp = sports(s)
  return {
    client_id: s.clientId,
    sort: i,
    name: s.name.trim() || `Space ${i + 1}`,
    space_type: str(s.spaceType),
    length_m: num(s.lengthM),
    width_m: num(s.widthM),
    height_m: num(s.heightM),
    mounting_height_m: num(s.mountingHeightM),
    working_plane_m: num(s.workingPlaneM),
    shape: s.shape,
    target_lux: s.luxUndecided ? null : num(s.targetLux),
    lux_undecided: s.luxUndecided,
    ceiling_type: str(s.ceilingType),
    finish_ceiling: str(s.finishCeiling),
    finish_walls: str(s.finishWalls),
    finish_floor: str(s.finishFloor),
    environment: s.environment,
    obstructions: {
      racking: s.racking,
      racking_height_m: s.racking ? num(s.rackingHeightM) : null,
      aisle_direction: s.racking ? str(s.aisleDirection) : null,
      machinery: s.machinery,
      mezzanine: s.mezzanine,
      ...(sp ? { sports: sp.data } : {}),
    },
    daylight: str(s.daylight),
    hours_per_day: num(s.hoursPerDay),
    days_per_week: num(s.daysPerWeek),
    existing_lighting: s.existingLighting
      .filter((f) => f.type.trim() || f.watts.trim() || f.qty.trim())
      .map((f) => ({ type: str(f.type), watts: num(f.watts), qty: num(f.qty) })),
    controls: s.controls,
    emergency: str(s.emergency),
    notes: str([sp?.line, s.notes.trim()].filter(Boolean).join('\n')),
    transcript: str(s.transcript),
  }
}

/** The body for submit_relux(). Files must already be uploaded. */
export function toSubmitPayload(d: ReluxDraft, files: UploadedFile[], completenessScore: number) {
  return {
    client_uuid: d.clientUuid,
    type: d.type,
    is_quick: d.isQuick,
    project_id: d.projectId ?? null,
    change_summary: str(d.changeSummary),
    follow_up_owner_id: str(d.followUpOwnerId),
    completeness_score: completenessScore,
    notes: str(d.notes),
    project: {
      name: d.project.name.trim(),
      site_address: str(d.project.siteAddress),
      site_suburb: str(d.project.siteSuburb),
      site_lat: d.project.siteLat ?? null,
      site_lng: d.project.siteLng ?? null,
      application: str(d.project.application),
      building_type: str(d.project.buildingType),
      project_type: str(d.project.projectType),
      project_stage: str(d.project.projectStage),
      budget_band: str(d.project.budgetBand),
      urgency: d.project.urgency,
      urgency_reason: d.project.urgency ? str(d.project.urgencyReason) : null,
      required_by: str(d.project.requiredBy),
    },
    contacts: d.contacts
      .filter((c) => c.contactId || c.fullName.trim())
      .map((c) => ({
        contact_id: c.contactId ?? null,
        full_name: c.fullName.trim(),
        company: str(c.company),
        role: str(c.role),
        email: str(c.email),
        phone: str(c.phone),
        is_primary: c.isPrimary,
      })),
    spaces: d.spaces.map(space),
    products: {
      mode: d.products.mode,
      alternatives: d.products.alternatives,
      items:
        d.products.mode === 'rep_products'
          ? d.products.items
              .filter((i) => i.productId || i.freeText.trim())
              .map((i) => ({
                product_id: i.productId ?? null,
                free_text: str(i.freeText),
                qty: num(i.qty),
                space_client_id: str(i.spaceClientId),
                notes: str(i.notes),
              }))
          : [],
    },
    files: files.map((f) => ({
      client_uuid: f.fileId,
      storage_path: f.storagePath,
      category: f.category,
      original_name: f.name,
      mime: f.mime,
      size: f.size,
      space_client_id: f.spaceClientId ?? null,
      markup: f.markup ?? null,
    })),
  }
}

/** Every local file the draft references, with its category and space. */
export function draftFiles(d: ReluxDraft) {
  const out: { fileId: string; category: string; spaceClientId?: string }[] = []
  for (const s of d.spaces) {
    for (const id of s.fileIds)
      out.push({ fileId: id, category: 'Photo', spaceClientId: s.clientId })
    for (const id of s.voiceNoteIds ?? [])
      out.push({ fileId: id, category: 'Voice Note', spaceClientId: s.clientId })
  }
  for (const id of d.generalFileIds) out.push({ fileId: id, category: 'Photo' })
  for (const id of d.generalVoiceIds ?? []) out.push({ fileId: id, category: 'Voice Note' })
  for (const doc of d.documents)
    out.push({ fileId: doc.fileId, category: doc.category, spaceClientId: doc.spaceClientId })
  return out
}
